import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { ToasterService } from '@ui-nuvertos/auth';
import { PdfApiService } from '../services/pdf-api.service';
import { AssetService } from '../services/asset/asset.service';

// QR Code size configurations
export interface QRSize {
  name: string;
  width: number;
  height: number;
  qrSize: number;
  fontSize: {
    title: number;
    code: number;
    info: number;
  };
}

export interface QRFieldConfig {
  key: string;
  label: string;
  selected: boolean;
  order: number;
}

export interface QRPrintData {
  asset_id: string;
  asset_code: string;
  asset_name: string;
  serial_number: string;
  barcode: string;
  tag_prefix: string;
  purchase_date?: string;
  qr_print_count: number;
  company_name?: string;
  company_logo?: string;
}

@Component({
  selector: 'app-qr-print-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    CheckboxModule,
    DragDropModule
  ],
  templateUrl: './qr-print-modal.component.html',
  styleUrls: ['./qr-print-modal.component.css']
})
export class QrPrintModalComponent implements OnInit {
  @Input() visible = false;
  @Input() assetId: string | null = null;
  @Input() assetData: any = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() printed = new EventEmitter<number>();

  // QR sizes configuration
  sizes: QRSize[] = [
    {
      name: 'Extra Small',
      width: 150,
      height: 180,
      qrSize: 80,
      fontSize: { title: 8, code: 10, info: 7 }
    },
    {
      name: 'Small',
      width: 200,
      height: 240,
      qrSize: 100,
      fontSize: { title: 10, code: 12, info: 8 }
    },
    {
      name: 'Medium',
      width: 280,
      height: 340,
      qrSize: 140,
      fontSize: { title: 12, code: 14, info: 10 }
    },
    {
      name: 'Large',
      width: 350,
      height: 420,
      qrSize: 180,
      fontSize: { title: 14, code: 16, info: 12 }
    }
  ];

  selectedSize: QRSize = this.sizes[1]; // Default to Small

  // Field configuration
  fields: QRFieldConfig[] = [
    { key: 'asset_code', label: 'Asset ID', selected: true, order: 0 },
    { key: 'purchase_date', label: 'Date of Purchase', selected: true, order: 1 },
    { key: 'tag_prefix', label: 'Asset Tag', selected: true, order: 2 },
    { key: 'serial_number', label: 'Serial No.', selected: true, order: 3 },
    { key: 'asset_name', label: 'Asset Name', selected: true, order: 4 }
  ];

  // QR data from API
  qrData: QRPrintData | null = null;
  isLoading = false;
  isPrinting = false;

  constructor(
    private assetService: AssetService,
    private toaster: ToasterService,
    private pdfService: PdfApiService
  ) {}

  ngOnInit(): void {}

  ngOnChanges(): void {
    if (this.visible && this.assetData) {
      this.populateQRData();
    }
  }

  populateQRData(): void {
    if (!this.assetData) return;
    
    const d = this.assetData;
    this.qrData = {
      asset_id: d.id,
      asset_code: d.asset_code,
      asset_name: d.asset_name,
      serial_number: d.serial_number,
      barcode: d.barcode,
      tag_prefix: d.tag_prefix,
      qr_print_count: d.qr_print_count || 0,
      purchase_date: d.detail?.purchase_date ? d.detail.purchase_date.split('T')[0] : '',
      company_name: d.company_name,
      company_logo: d.company_logo
    };
  }

  selectSize(size: QRSize): void {
    this.selectedSize = size;
  }

  onFieldDrop(event: CdkDragDrop<QRFieldConfig[]>): void {
    moveItemInArray(this.fields, event.previousIndex, event.currentIndex);
    // Update order
    this.fields.forEach((field, index) => {
      field.order = index;
    });
  }

  getQRCodeUrl(): string {
    if (!this.qrData) return '';
    // Generate QR code URL using a QR code API or library
    // Using the asset ID as the QR content
    const qrContent = encodeURIComponent(this.qrData.asset_id);
    return `https://api.qrserver.com/v1/create-qr-code/?size=${this.selectedSize.qrSize}x${this.selectedSize.qrSize}&data=${qrContent}`;
  }

  getSelectedFields(): QRFieldConfig[] {
    return this.fields
      .filter(f => f.selected)
      .sort((a, b) => a.order - b.order);
  }

  getFieldValue(key: string): string {
    if (!this.qrData) return '';
    switch (key) {
      case 'asset_code':
        return this.qrData.asset_code || '';
      case 'asset_name':
        return this.qrData.asset_name || '';
      case 'serial_number':
        return this.qrData.serial_number || '';
      case 'tag_prefix':
        return this.qrData.tag_prefix || '';
      case 'purchase_date':
        return this.qrData.purchase_date || '';
      case 'barcode':
        return this.qrData.barcode || '';
      default:
        return '';
    }
  }

  closeModal(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  print(): void {
    if (!this.assetId || !this.qrData) return;

    this.isPrinting = true;
    
    // Optimistically update count
    const newCount = (this.qrData.qr_print_count || 0) + 1;
    this.qrData.qr_print_count = newCount;
    this.printed.emit(newCount);

    // Call print-data API (which now increments the count on backend)
    const payload = {
      size: this.selectedSize.name,
      fields: this.getSelectedFields().map(f => f.key)
    };

    this.assetService.getPrintData(this.assetId, payload)
      .subscribe({
        next: (printData) => {
          if (printData.content) {
            printData.content['qr_code'] = this.getQRCodeUrl();
          }

          // Generate PDF using the data
          const generateRequest = {
            template_id: printData.template_id,
            content: printData.content,
            filename: printData.filename,
            margin_inch: 0, 
          };

          this.pdfService.generateAndOpenInNewTab(generateRequest);
          this.isPrinting = false;
        },
        error: (err) => {
          this.toaster.error('Failed to get print data');
          // Revert optimistic update if needed, but for print count it's minor
          this.isPrinting = false;
        }
      });
  }

}
